import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { canGoBack } from "../../../shared/phone/history";
import { useRail } from "../../../shared/phone/media";
import { PhoneNav, type PhoneBack } from "../../../shared/phone/PhoneNav";
import { Icon } from "../../components/ui/Icon";
import { api } from "../../lib/api";
import { initials } from "./text";

/** "Step 2 of 6", as the new goal's bar says it; the audience and ads steps count the same way. */
export const stepLine = (step: number, total: number) => `Step ${step} of ${total}`;

/** Cancel in a flow's bar: back where the person came from, or Home when the flow was opened from a link. */
export function CancelButton({ home = "/command" }: { home?: string }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="pn-tbtn" onClick={() => (canGoBack() ? navigate(-1) : navigate(home, { replace: true }))}>
      Cancel
    </button>
  );
}

/** The new goal's own bar: "New goal" with the step under it, Cancel on the first step and back after. */
export function FlowBar({ sub, back, cancel = false }: { sub: string; back?: PhoneBack; cancel?: boolean }) {
  return (
    <PhoneNav
      title={
        <span className="su-ct">
          <b>New goal</b>
          <span>{sub}</span>
        </span>
      }
      back={cancel ? undefined : back}
      left={cancel ? <CancelButton /> : undefined}
    />
  );
}

/** A step's heading and its one line, at the top of the step. */
export function StepHead({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <div className="su-wh">
      <h2>{title}</h2>
      {sub ? <p>{sub}</p> : null}
    </div>
  );
}

/**
 * The steps down the side on an iPad, with a tick on each step done. A step done that can still be changed
 * opens it; the rest only show where the goal is.
 */
export function StepRail({ steps, step, canGo, onGo }: { steps: readonly string[]; step: number; canGo?: (i: number) => boolean; onGo?: (i: number) => void }) {
  const rail = useRail();
  if (!rail) return null;
  return (
    <nav className="su-rail" aria-label="Steps">
      {step > 0 &&
        steps.map((label, k) => {
          const i = k + 1;
          const done = i < step;
          const open = done && !!canGo?.(i) && !!onGo;
          return (
            <button key={label} type="button" className={`su-rst${done ? " ok" : ""}${i === step ? " on" : ""}`} disabled={!open} aria-current={i === step ? "step" : undefined} onClick={open ? () => onGo?.(i) : undefined}>
              <span className="su-rsn">{done ? <Icon name="check" /> : i}</span>
              <span>{label}</span>
            </button>
          );
        })}
    </nav>
  );
}

/** A product's square: its logo, or its initials on the app's product colour. */
export function ProductTile({ product, size = 32 }: { product: { id: string; name: string; logoR2Key: string | null }; size?: number }) {
  const style = { width: size, height: size, fontSize: Math.round(size * (initials(product.name).length > 1 ? 0.34 : 0.42)) };
  if (product.logoR2Key) return <img className="su-tile" src={api.products.logoUrl(product.id)} alt="" style={style} />;
  return (
    <span className="su-tile" style={style} aria-hidden="true">
      {initials(product.name)}
    </span>
  );
}

/** The one main action at the bottom of a step, with the reason it waits just above it. */
export function Foot({ children, why }: { children: ReactNode; why?: string | null }) {
  return (
    <div className="pn-foot su-foot">
      {why ? (
        <p className="su-why">
          <Icon name="clock" />
          {why}
        </p>
      ) : null}
      {children}
    </div>
  );
}

/** A spinner the size of the text around it. */
export const Spin = () => <span className="su-spin" aria-hidden="true" />;
